import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { History, Plus, Pencil, Trash2, User, Package, Wrench, FolderTree, MapPin, Truck, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const entityIcons = {
  part: Package,
  tool: Wrench,
  category: FolderTree,
  location: MapPin,
  supplier: Truck,
  user: User,
};

const actionColors = {
  create: 'bg-emerald-100 text-emerald-700',
  update: 'bg-blue-100 text-blue-700',
  delete: 'bg-red-100 text-red-700',
};

const AuditLog = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterEntity, setFilterEntity] = useState('all');
  const { isAdmin } = useAuth();

  useEffect(() => {
    if (isAdmin()) {
      fetchLogs();
    }
  }, [filterEntity]);

  const fetchLogs = async () => {
    try {
      const params = filterEntity !== 'all' ? `?entity_type=${filterEntity}` : '';
      const response = await axios.get(`${API}/audit-logs${params}`);
      setLogs(response.data);
    } catch (error) {
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'create':
        return <Plus className="w-4 h-4" />;
      case 'update':
        return <Pencil className="w-4 h-4" />;
      case 'delete':
        return <Trash2 className="w-4 h-4" />;
      default:
        return null;
    }
  };

  const getEntityIcon = (entityType) => {
    const Icon = entityIcons[entityType] || Package;
    return <Icon className="w-4 h-4" />;
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return {
      date: date.toLocaleDateString(),
      time: date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };
  };

  const formatChanges = (changes) => {
    if (!changes) return null;
    const entries = Object.entries(changes).filter(([key]) => key !== 'updated_at');
    if (entries.length === 0) return null;
    return entries.map(([key, value]) => (
      <span key={key} className="inline-block mr-2">
        <span className="text-slate-500">{key.replace(/_/g, ' ')}:</span>{' '}
        <span className="font-medium">{typeof value === 'object' ? JSON.stringify(value) : String(value)}</span>
      </span>
    ));
  };

  // Restrict access to admins only
  if (!isAdmin()) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-slate-500">
        <ShieldAlert className="w-16 h-16 mb-4 text-slate-300" />
        <h2 className="text-xl font-semibold text-slate-700">Access Restricted</h2>
        <p className="mt-2">Only administrators can view the audit log.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="audit-log-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight">Audit Log</h1>
          <p className="text-slate-500 mt-1">Track all changes in the system</p>
        </div>
        <Select value={filterEntity} onValueChange={setFilterEntity}>
          <SelectTrigger className="w-48 rounded-sm" data-testid="audit-entity-filter">
            <SelectValue placeholder="Filter by type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="part">Parts</SelectItem>
            <SelectItem value="tool">Tools</SelectItem>
            <SelectItem value="category">Categories</SelectItem>
            <SelectItem value="location">Locations</SelectItem>
            <SelectItem value="supplier">Suppliers</SelectItem>
            <SelectItem value="user">Users</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card className="border-slate-200 shadow-sm rounded-sm">
        <CardContent className="p-0">
          {logs.length > 0 ? (
            <div className="divide-y divide-slate-100">
              {logs.map((log) => {
                const { date, time } = formatTimestamp(log.timestamp);
                return (
                  <div
                    key={log.id}
                    className="p-4 hover:bg-slate-50 transition-colors"
                    data-testid={`audit-log-${log.id}`}
                  >
                    <div className="flex items-start gap-4">
                      {/* Timeline indicator */}
                      <div className="flex flex-col items-center">
                        <div className={`w-8 h-8 rounded-sm flex items-center justify-center ${actionColors[log.action]}`}>
                          {getActionIcon(log.action)}
                        </div>
                        <div className="w-px h-full bg-slate-200 mt-2" />
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-slate-900">{log.user_name}</span>
                          <Badge className={`rounded-sm text-xs ${actionColors[log.action]}`}>
                            {log.action}d
                          </Badge>
                          <div className="flex items-center gap-1 text-slate-600">
                            {getEntityIcon(log.entity_type)}
                            <span className="capitalize">{log.entity_type}</span>
                          </div>
                          <span className="font-mono text-sm font-semibold text-slate-900">
                            "{log.entity_name}"
                          </span>
                        </div>
                        
                        {log.changes && (
                          <div className="mt-2 text-sm text-slate-600 bg-slate-50 rounded-sm p-2">
                            {formatChanges(log.changes)}
                          </div>
                        )}
                      </div>

                      {/* Timestamp */}
                      <div className="text-right flex-shrink-0">
                        <p className="text-sm font-mono text-slate-900">{time}</p>
                        <p className="text-xs text-slate-500">{date}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 text-slate-400">
              <History className="w-12 h-12 mb-4" />
              <p className="text-lg font-medium">No audit logs yet</p>
              <p className="text-sm">Changes will appear here</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AuditLog;
